import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Send, Loader2, Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon,
  Reply, ReplyAll, Forward, Paperclip, X,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { ExecInboxStage } from '../../types';
import { ExecMessage, sendExecReply, ForwardAttachmentRef } from '../../utils/execInboxApi';

export type ComposeMode = 'reply' | 'replyAll' | 'forward';

export function textToHtml(text: string): string {
  const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return esc.replace(/\n/g, '<br>');
}

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Parse a raw address header ("Name <a@b.com>, c@d.com") into lowercase emails. */
export function parseAddressList(raw: string): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => {
      const m = s.match(/<([^>]+)>/);
      return (m ? m[1] : s).trim().toLowerCase();
    })
    .filter((s) => !!s && s.includes('@'));
}

function buildForwardBody(m: ExecMessage): string {
  const original = m.bodyHtml
    ? DOMPurify.sanitize(m.bodyHtml, { USE_PROFILES: { html: true } })
    : textToHtml(m.bodyText || m.snippet || '');
  const fromLine = m.fromName ? `${m.fromName} <${m.from}>` : m.from;
  return (
    '<br><br><div style="color:#64748B;font-size:12px;">---------- Forwarded message ----------<br>'
    + `From: ${escHtml(fromLine)}<br>`
    + `Date: ${escHtml(m.date || '')}<br>`
    + `Subject: ${escHtml(m.subject || '')}<br>`
    + `To: ${escHtml(m.to || '')}</div><br>`
    + original
  );
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Recipient chip input
// ---------------------------------------------------------------------------

interface ChipInputProps {
  id: string;
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}

const ChipInput: React.FC<ChipInputProps> = ({ id, values, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const parts = draft.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter((s) => s.includes('@'));
    if (parts.length) onChange(Array.from(new Set([...values, ...parts])));
    setDraft('');
  };

  return (
    <div className="exec-chips" onClick={() => document.getElementById(id)?.focus()}>
      {values.map((v) => (
        <span key={v} className="exec-chip">
          {v}
          <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}>
            <X size={10} />
          </button>
        </span>
      ))}
      <input
        id={id}
        className="exec-chip-input"
        value={draft}
        placeholder={values.length ? '' : placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
          if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

export interface ExecComposerProps {
  mailboxUserId: string;
  threadId: string;
  threadSubject: string;
  /** Message this compose action anchors to (usually the latest). */
  anchor: ExecMessage | null;
  fallbackReplyTo: string;
  mode: ComposeMode;
  onModeChange: (m: ComposeMode) => void;
  /** Bumped by the parent to force re-initialization (e.g. per-message action). */
  composeSeq: number;
  ownerName: string;
  ownerEmail: string;
  senderName: string;
  isOwner: boolean;
  stages: ExecInboxStage[];
  /** HTML injected from an agent draft ("Use draft"). */
  injectedHtml?: { html: string; seq: number } | null;
  onSent: () => void | Promise<void>;
  onToast: (msg: string, error?: boolean) => void;
}

const MODE_META: { id: ComposeMode; label: string; icon: React.ReactNode }[] = [
  { id: 'reply', label: 'Reply', icon: <Reply size={13} /> },
  { id: 'replyAll', label: 'Reply all', icon: <ReplyAll size={13} /> },
  { id: 'forward', label: 'Forward', icon: <Forward size={13} /> },
];

const ExecComposer: React.FC<ExecComposerProps> = ({
  mailboxUserId, threadId, threadSubject, anchor, fallbackReplyTo, mode, onModeChange, composeSeq,
  ownerName, ownerEmail, senderName, isOwner, stages, injectedHtml, onSent, onToast,
}) => {
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [moveToStage, setMoveToStage] = useState('responded');
  const [fwdSelected, setFwdSelected] = useState<Record<string, boolean>>({});
  const editorRef = useRef<HTMLDivElement>(null);

  const ownerNorm = ownerEmail.trim().toLowerCase();

  // Initialize recipients / subject / body when the compose context changes.
  useEffect(() => {
    const subj = threadSubject || anchor?.subject || '';
    const bare = subj.replace(/^\s*(re|fwd?):\s*/i, '');
    if (mode === 'forward') {
      setTo([]);
      setCc([]);
      setSubject(/^fwd?:/i.test(subj) ? subj : `Fwd: ${bare}`);
      const sel: Record<string, boolean> = {};
      (anchor?.attachments || []).forEach((a) => { sel[a.id] = true; });
      setFwdSelected(sel);
      if (editorRef.current) editorRef.current.innerHTML = anchor ? buildForwardBody(anchor) : '';
    } else {
      const inboundFrom = anchor && anchor.from.toLowerCase() !== ownerNorm ? anchor.from.toLowerCase() : '';
      if (mode === 'replyAll' && anchor) {
        const all = new Set<string>([
          ...(inboundFrom ? [inboundFrom] : []),
          ...parseAddressList(anchor.to),
        ]);
        all.delete(ownerNorm);
        const ccList = parseAddressList(anchor.cc).filter((e) => e !== ownerNorm && !all.has(e));
        setTo(all.size ? Array.from(all) : (fallbackReplyTo ? [fallbackReplyTo.toLowerCase()] : []));
        setCc(ccList);
        if (ccList.length) setShowCc(true);
      } else {
        setTo(inboundFrom ? [inboundFrom] : (fallbackReplyTo ? [fallbackReplyTo.toLowerCase()] : []));
        setCc([]);
      }
      setSubject(/^re:/i.test(subj) ? subj : `Re: ${bare}`);
      setFwdSelected({});
      if (editorRef.current) editorRef.current.innerHTML = '';
    }
    setBcc([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, composeSeq, anchor?.id]);

  // Agent draft injection.
  useEffect(() => {
    if (injectedHtml && editorRef.current) {
      editorRef.current.innerHTML = DOMPurify.sanitize(injectedHtml.html, { USE_PROFILES: { html: true } });
      editorRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injectedHtml?.seq]);

  const exec = (cmd: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const addLink = () => {
    const url = window.prompt('Link URL:');
    if (url) exec('createLink', /^https?:\/\//i.test(url) ? url : `https://${url}`);
  };

  const fwdAttachments: ForwardAttachmentRef[] = useMemo(() => {
    if (mode !== 'forward' || !anchor?.attachments) return [];
    return anchor.attachments
      .filter((a) => fwdSelected[a.id])
      .map((a) => ({ messageId: anchor.id, attachmentId: a.id, filename: a.filename }));
  }, [mode, anchor, fwdSelected]);

  const fwdTotalBytes = useMemo(() => {
    if (mode !== 'forward' || !anchor?.attachments) return 0;
    return anchor.attachments.filter((a) => fwdSelected[a.id]).reduce((s, a) => s + (a.size || 0), 0);
  }, [mode, anchor, fwdSelected]);

  const handleSend = async () => {
    const bodyHtml = editorRef.current?.innerHTML || '';
    const bodyText = editorRef.current?.innerText || '';
    if (!to.length) { onToast('Add at least one recipient', true); return; }
    if (!subject.trim()) { onToast('Subject is required', true); return; }
    if (!bodyText.trim() && !fwdAttachments.length) { onToast('Write a message first', true); return; }
    if (fwdTotalBytes > 25 * 1024 * 1024) { onToast('Attachments exceed the 25 MB limit', true); return; }
    setSending(true);
    try {
      await sendExecReply(mailboxUserId, {
        to,
        cc: cc.length ? cc : undefined,
        bcc: bcc.length ? bcc : undefined,
        subject,
        bodyHtml: DOMPurify.sanitize(bodyHtml, { USE_PROFILES: { html: true } }),
        bodyText,
        threadId,
        inReplyTo: mode !== 'forward' ? anchor?.messageIdHeader : undefined,
        references: mode !== 'forward'
          ? [anchor?.references, anchor?.messageIdHeader].filter(Boolean).join(' ')
          : undefined,
        moveToStage,
        forwardAttachments: fwdAttachments.length ? fwdAttachments : undefined,
      });
      if (editorRef.current) editorRef.current.innerHTML = '';
      await onSent();
    } catch (e) {
      onToast((e as Error).message, true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="exec-composer">
      <div className="exec-compose-tabs">
        {MODE_META.map((m) => (
          <button
            key={m.id}
            type="button"
            className={`exec-compose-tab${mode === m.id ? ' active' : ''}`}
            onClick={() => onModeChange(m.id)}
          >
            {m.icon} {m.label}
          </button>
        ))}
        <button type="button" className="exec-cc-toggle" onClick={() => setShowCc((v) => !v)}>
          Cc/Bcc
        </button>
      </div>

      <div className="exec-composer-row">
        <label htmlFor="exec-to">To</label>
        <ChipInput id="exec-to" values={to} onChange={setTo} placeholder="Add recipients…" />
      </div>
      {showCc && (
        <>
          <div className="exec-composer-row">
            <label htmlFor="exec-cc">Cc</label>
            <ChipInput id="exec-cc" values={cc} onChange={setCc} />
          </div>
          <div className="exec-composer-row">
            <label htmlFor="exec-bcc">Bcc</label>
            <ChipInput id="exec-bcc" values={bcc} onChange={setBcc} />
          </div>
        </>
      )}
      <div className="exec-composer-row">
        <label htmlFor="exec-subj">Subject</label>
        <input id="exec-subj" className="exec-composer-input" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>

      <div className="exec-rt-toolbar">
        <button type="button" className="exec-rt-btn" title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')}><Bold size={13} /></button>
        <button type="button" className="exec-rt-btn" title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')}><Italic size={13} /></button>
        <button type="button" className="exec-rt-btn" title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')}><Underline size={13} /></button>
        <span className="exec-rt-sep" />
        <button type="button" className="exec-rt-btn" title="Bulleted list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')}><List size={13} /></button>
        <button type="button" className="exec-rt-btn" title="Numbered list" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')}><ListOrdered size={13} /></button>
        <span className="exec-rt-sep" />
        <button type="button" className="exec-rt-btn" title="Insert link" onMouseDown={(e) => e.preventDefault()} onClick={addLink}><LinkIcon size={13} /></button>
      </div>
      <div
        ref={editorRef}
        className="exec-richtext"
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Message body"
        data-placeholder={mode === 'forward' ? 'Add a note…' : 'Write your reply…'}
        suppressContentEditableWarning
      />

      {mode === 'forward' && (anchor?.attachments?.length || 0) > 0 && (
        <div className="exec-fwd-atts">
          <div className="exec-fwd-atts-head">
            <Paperclip size={12} /> Include original attachments
            <span className="exec-fwd-atts-total">{fmtSize(fwdTotalBytes)} selected</span>
          </div>
          {anchor!.attachments!.map((a) => (
            <label key={a.id} className="exec-fwd-att">
              <input
                type="checkbox"
                checked={!!fwdSelected[a.id]}
                onChange={(e) => setFwdSelected((s) => ({ ...s, [a.id]: e.target.checked }))}
              />
              <span className="exec-attach-name" title={a.filename}>{a.filename}</span>
              <span className="exec-attach-size">{fmtSize(a.size)}</span>
            </label>
          ))}
        </div>
      )}

      <div className="exec-composer-foot">
        <div className="exec-attribution">
          Sending as <strong>{ownerName} &lt;{ownerEmail}&gt;</strong>
          {!isOwner && <> · shows <strong>sent by {senderName}</strong></>}
        </div>
        <div className="exec-send-controls">
          <label className="exec-attribution" htmlFor="exec-move">then</label>
          <select id="exec-move" className="exec-mini-select" value={moveToStage} onChange={(e) => setMoveToStage(e.target.value)}>
            {stages.filter((s) => !s.archived).sort((a, b) => a.order - b.order).map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
          <button className="exec-send-btn" onClick={handleSend} disabled={sending}>
            {sending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
            {sending ? 'Sending…' : mode === 'forward' ? 'Forward' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecComposer;
